import React, { useState, useEffect } from "react";
import styled from "styled-components";

export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [fontSize, setFontSize] = useState(0);       // steps: -1, 0, +1, +2
  const [highContrast, setHighContrast] = useState(false);
  const [highlightLinks, setHighlightLinks] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const base = 16;
    root.style.fontSize = `${base + fontSize * 2}px`;
  }, [fontSize]);

  useEffect(() => {
    if (highContrast) {
      document.body.classList.add("high-contrast");
    } else {
      document.body.classList.remove("high-contrast");
    }
  }, [highContrast]);

  useEffect(() => {
    if (highlightLinks) {
      document.body.classList.add("highlight-links");
    } else {
      document.body.classList.remove("highlight-links");
    }
  }, [highlightLinks]);

  const reset = () => {
    setFontSize(0);
    setHighContrast(false);
    setHighlightLinks(false);
  };

  return (
    <>
      {/* Inject global styles for accessibility modes */}
      <style>{`
        body.high-contrast {
          filter: contrast(150%) brightness(1.05);
        }
        body.highlight-links a,
        body.highlight-links button {
          outline: 2px solid var(--rose-600) !important;
          outline-offset: 2px !important;
        }
      `}</style>

      <FloatingBtn
        aria-label="כלי נגישות"
        title="כלי נגישות"
        onClick={() => setOpen((o) => !o)}
        className="pinkBg"
      >
        ♿
      </FloatingBtn>

      {open && (
        <Panel role="dialog" aria-label="הגדרות נגישות" className="darkBg">
          <PanelTitle className="whiteColor font15 semiBold">הגדרות נגישות</PanelTitle>

          <OptionRow>
            <OptionLabel className="whiteColor font13">גודל טקסט</OptionLabel>
            <BtnGroup>
              <StepBtn
                aria-label="הקטן טקסט"
                disabled={fontSize <= -1}
                onClick={() => setFontSize((f) => f - 1)}
              >
                A-
              </StepBtn>
              <StepBtn
                aria-label="אפס גודל טקסט"
                onClick={() => setFontSize(0)}
                style={{ fontWeight: fontSize === 0 ? 800 : 400 }}
              >
                A
              </StepBtn>
              <StepBtn
                aria-label="הגדל טקסט"
                disabled={fontSize >= 3}
                onClick={() => setFontSize((f) => f + 1)}
              >
                A+
              </StepBtn>
            </BtnGroup>
          </OptionRow>

          <OptionRow>
            <OptionLabel className="whiteColor font13">ניגודיות גבוהה</OptionLabel>
            <ToggleBtn
              aria-pressed={highContrast}
              active={highContrast}
              onClick={() => setHighContrast((v) => !v)}
            >
              {highContrast ? "פעיל" : "כבוי"}
            </ToggleBtn>
          </OptionRow>

          <OptionRow>
            <OptionLabel className="whiteColor font13">הדגשת קישורים</OptionLabel>
            <ToggleBtn
              aria-pressed={highlightLinks}
              active={highlightLinks}
              onClick={() => setHighlightLinks((v) => !v)}
            >
              {highlightLinks ? "פעיל" : "כבוי"}
            </ToggleBtn>
          </OptionRow>

          <ResetBtn onClick={reset} className="font13">
            איפוס הכל
          </ResetBtn>
        </Panel>
      )}
    </>
  );
}

const FloatingBtn = styled.button`
  position: fixed;
  bottom: 30px;
  left: 20px;
  z-index: 9000;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: none;
  font-size: 1.4rem;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(73, 8, 57,0.25);
  transition: transform 0.6s var(--ease-spring), box-shadow 0.6s var(--ease-out);
  &:hover { transform: scale(1.1); }
  &:focus { outline: 3px solid var(--butter-300); outline-offset: 3px; }
`;
const Panel = styled.div`
  position: fixed;
  bottom: 90px;
  left: 20px;
  z-index: 9000;
  width: 230px;
  border-radius: 1rem;
  padding: 20px;
  box-shadow: 0 8px 30px rgba(73, 8, 57,0.3);
  direction: rtl;
`;
const PanelTitle = styled.p`
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(203, 29, 133, 0.3);
  padding-bottom: 10px;
`;
const OptionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;
const OptionLabel = styled.span``;
const BtnGroup = styled.div`
  display: flex;
  gap: 4px;
`;
const StepBtn = styled.button`
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(203, 29, 133, 0.3);
  color: var(--rose-700);
  border-radius: 4px;
  width: 30px;
  height: 28px;
  font-size: 0.75rem;
  cursor: pointer;
  &:hover:not(:disabled) { background: rgba(203, 29, 133, 0.3); }
  &:disabled { opacity: 0.3; cursor: default; }
`;
const ToggleBtn = styled.button`
  background: ${(p) => (p.active ? "var(--butter-300)" : "rgba(255, 255, 255, 0.6)")};
  border: 1px solid rgba(203, 29, 133, 0.3);
  color: ${(p) => (p.active ? "var(--ink)" : "var(--rose-700)")};
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${(p) => (p.active ? "var(--butter-200)" : "rgba(255, 255, 255, 0.9)")}; }
`;
const ResetBtn = styled.button`
  width: 100%;
  margin-top: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(203, 29, 133, 0.3);
  color: var(--rose-700);
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.5); }
`;
